import React, { useState } from 'react';
import { Button } from './ui/button';
import { Smile } from 'lucide-react';

interface EmojiPickerProps {
  value: string;
  onChange: (emoji: string) => void;
}

const EMOJI_CATEGORIES = {
  'Rostos': ['😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊', '😇', '🙂', '🙃', '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚', '😋', '😛', '😝', '😜', '🤪', '🤨', '🧐', '🤓', '😎', '🤩', '🥳'],
  'Pessoas': ['👤', '👥', '👨', '👩', '👶', '👦', '👧', '🧒', '👨‍💼', '👩‍💼', '👨‍💻', '👩‍💻', '👨‍🎓', '👩‍🎓', '👨‍🏫', '👩‍🏫', '👨‍⚖️', '👩‍⚖️', '👨‍🔬', '👩‍🔬'],
  'Gestos': ['👍', '👎', '👊', '✊', '🤛', '🤜', '🤝', '👏', '🙌', '👐', '🤲', '🙏', '✍️', '💪', '👈', '👉', '👆', '👇', '☝️', '✋', '🤚', '🖐️', '🖖', '👋', '🤙', '💅'],
  'Objetos': ['💼', '📁', '📂', '🗂️', '📅', '📆', '🗒️', '📝', '📋', '📊', '📈', '📉', '🗃️', '🗄️', '📇', '💾', '💿', '📀', '🖥️', '💻', '⌨️', '🖨️', '🖱️', '🗜️', '💡', '🔦', '🕯️', '📡', '🔋', '🔌'],
  'Ferramentas': ['🔨', '⚒️', '🛠️', '⛏️', '🔧', '🔩', '⚙️', '🗜️', '⚗️', '🧪', '🧬', '🔬', '🔭', '📡', '💉', '🩺', '🩹', '🩼', '🧲', '🧰'],
  'Comunicação': ['📱', '📲', '☎️', '📞', '📟', '📠', '📧', '📨', '📩', '📤', '📥', '📦', '📫', '📪', '📬', '📭', '📮', '📯', '📢', '📣', '📻', '📺', '🎙️', '🎚️', '🎛️'],
  'Escrita': ['✏️', '✒️', '🖋️', '🖊️', '🖌️', '🖍️', '📝', '✍️', '📄', '📃', '📑', '📜', '📋', '📰', '📖', '📚', '📙', '📘', '📗', '📕', '📓', '📔'],
  'Dinheiro': ['💰', '💴', '💵', '💶', '💷', '💸', '💳', '💹', '💱', '💲', '🏦', '🏧', '💎', '⚖️', '🧾', '💼', '📈', '📉'],
  'Arte': ['🎨', '🖼️', '🖌️', '🖍️', '✏️', '📐', '📏', '🎭', '🎪', '🎬', '🎤', '🎧', '🎼', '🎹', '🥁', '🎷', '🎺', '🎸', '🎻', '🎲'],
  'Tecnologia': ['💻', '🖥️', '⌨️', '🖱️', '🖨️', '📱', '📲', '💾', '💿', '📀', '🧮', '🎮', '🕹️', '🗜️', '💡', '🔦', '🕯️', '🔌', '🔋', '📡'],
  'Ciência': ['🔬', '🔭', '🧪', '🧬', '🧫', '🧬', '⚗️', '🧲', '🧪', '💉', '🩺', '⚛️', '🔭', '📡', '🛰️', '🚀', '🛸'],
  'Comida': ['🍔', '🍕', '🌮', '🌯', '🥙', '🥪', '🌭', '🍿', '🧈', '🥞', '🧇', '🥓', '🥩', '🍗', '🍖', '🦴', '🌭', '🍔', '🍟', '🍕'],
  'Natureza': ['🌱', '🌿', '☘️', '🍀', '🎋', '🎍', '🌾', '🌱', '🌳', '🌲', '🌴', '🌵', '🌾', '🌿', '☘️', '🍃', '🍂', '🍁', '🍄', '🌰'],
  'Animais': ['🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯', '🦁', '🐮', '🐷', '🐸', '🐵', '🐔', '🐧', '🐦', '🦆', '🦅'],
  'Símbolos': ['✅', '❌', '⭐', '🌟', '✨', '⚡', '🔥', '💧', '🌈', '☀️', '🌙', '⭐', '🌟', '💫', '✨', '☄️', '🌠', '🔔', '🔕', '🎵'],
  'Outros': ['🤖', '👽', '👾', '🎯', '🎪', '🎨', '🎬', '🎤', '🎧', '🎼', '🎹', '🥁', '🎷', '🎺', '🎸', '🎻', '🎲', '🎯', '🎳', '🎮']
};

export function EmojiPicker({ value, onChange }: EmojiPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('Rostos');

  const handleSelect = (emoji: string) => {
    onChange(emoji);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      {/* Input com Preview */}
      <div className="flex gap-2">
        <div 
          className="w-12 h-12 flex items-center justify-center text-2xl border-2 border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors"
          onClick={() => setIsOpen(!isOpen)}
        >
          {value || '🤖'}
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => setIsOpen(!isOpen)}
          className="flex-1"
        >
          <Smile className="w-4 h-4 mr-2" />
          Selecionar Emoji
        </Button>
      </div>

      {/* Picker Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Picker */}
          <div className="absolute top-full left-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
            {/* Header */}
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">Escolher Emoji</h3>
            </div>

            {/* Categories */}
            <div className="flex overflow-x-auto border-b border-gray-200 px-2 py-2 gap-1">
              {Object.keys(EMOJI_CATEGORIES).map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition-colors ${
                    selectedCategory === category
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* Emoji Grid */}
            <div className="p-4 max-h-64 overflow-y-auto">
              <div className="grid grid-cols-8 gap-2">
                {EMOJI_CATEGORIES[selectedCategory as keyof typeof EMOJI_CATEGORIES].map((emoji) => (
                  <button
                    key={emoji}
                    onClick={() => handleSelect(emoji)}
                    className={`w-10 h-10 flex items-center justify-center text-2xl rounded-lg hover:bg-gray-100 transition-colors ${
                      value === emoji ? 'bg-blue-100 ring-2 ring-blue-500' : ''
                    }`}
                    title={emoji}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-gray-200 bg-gray-50 text-xs text-gray-500 text-center">
              Clique em um emoji para selecionar
            </div>
          </div>
        </>
      )}
    </div>
  );
}

